import { useLanguage } from "@/i18n";
import Reveal from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MapPin, Clock, Shirt, Mail, MessageCircle } from "lucide-react";

const IMG_DRINKS = "https://images.pexels.com/photos/31415055/pexels-photo-31415055.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Practical() {
  const { t } = useLanguage();
  const info = [
    { icon: MapPin, label: t.practical.location, value: t.practical.locationVal },
    { icon: Clock, label: t.practical.times, value: t.practical.timesVal },
    { icon: Shirt, label: t.practical.dress, value: t.practical.dressVal },
    { icon: Mail, label: t.practical.contact, value: t.practical.email },
  ];

  return (
    <section id="practical" data-testid="practical-section" className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-ocean mb-4">{t.practical.kicker}</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-12">{t.practical.title}</h2>
      </Reveal>

      <div className="grid lg:grid-cols-2 gap-12">
        <div className="space-y-4">
          {info.map((item, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="flex items-center gap-4 rounded-2xl border border-stroke bg-surface p-5 hover:border-ocean/50 transition-colors duration-300" data-testid={`practical-info-${i}`}>
                <span className="w-11 h-11 rounded-full bg-ocean/10 text-ocean flex items-center justify-center shrink-0">
                  <item.icon size={18} />
                </span>
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{item.label}</p>
                  <p className="font-semibold text-sm sm:text-base mt-0.5">{item.value}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.25}>
            <a href="https://wa.me/31640523229" target="_blank" rel="noopener noreferrer" data-testid="whatsapp-button"
              className="inline-flex items-center gap-2.5 rounded-full border border-ocean/50 bg-ocean/10 text-ocean font-semibold text-sm px-6 py-3 hover:bg-ocean hover:text-night transition-colors duration-300">
              <MessageCircle size={16} /> {t.practical.whatsapp} · 06-40523229
            </a>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="rounded-2xl overflow-hidden border border-stroke mt-2">
              <img src={IMG_DRINKS} alt="Beach drinks" className="w-full h-44 object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <h3 className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-4">{t.practical.faqTitle}</h3>
          <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
            {t.practical.faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="rounded-2xl border border-stroke bg-surface px-5 overflow-hidden">
                <AccordionTrigger className="text-sm sm:text-base font-semibold hover:text-sunset hover:no-underline py-4" data-testid={`faq-question-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-400 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}
